/** @format */
import { Trans, useTranslation } from 'next-i18next';
import React, { FunctionComponent, useState } from 'react';
import styled from 'styled-components';

import { trackViaDataLayer } from '@toucan/client/shared/helpers/analytics';

import {
    DEFAULT_TEST_ID,
    JOURNEY_FEEDBACK_EVENT,
    JourneyFeedbackVerdict,
} from './config/constants';
import { JourneyFeedbackProps } from './config/types';
import { styles } from './journey-feedback.styles';

export * from './config/constants';
export type { JourneyFeedbackProps } from './config/types';

const StyledJourneyFeedback = styled.div`
    ${styles}
`;

/**
 * The tick that appears beside a pressed button's label, drawn on rather than
 * simply shown.
 *
 * Inline rather than an imported asset so the stroke is addressable from the
 * stylesheet, which is what lets it be drawn. It is always in the button's flow,
 * just invisible until pressed, so appearing costs the button no width and moves
 * nothing beside it.
 */
const FeedbackTick: FunctionComponent = () => (
    <svg
        className="journey-feedback-tick"
        viewBox="0 0 24 24"
        aria-hidden="true"
        focusable="false"
    >
        <path
            className="journey-feedback-tick-mark"
            d="M5 13 L9.5 17.5 L19 7"
            fill="none"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

/**
 * Asks whether the section above it was any use, and reports the answer.
 *
 * The answer is held in memory only. It is a signal for the reports rather than a
 * preference worth remembering, so a reader who reloads the page is asked again —
 * and nothing has to be stored about them to make that work.
 */
export const JourneyFeedback: FunctionComponent<JourneyFeedbackProps> = ({
    modelId,
    'data-testid': dataTestId = DEFAULT_TEST_ID,
    ...otherProps
}) => {
    const { t } = useTranslation();
    const [verdict, setVerdict] = useState<JourneyFeedbackVerdict | null>(null);
    const hasAnswered = verdict !== null;

    /**
     * Both buttons stay live after an answer, so a reader who decides they were
     * wrong can say so. Pressing the answer they already gave is not a change of
     * mind and reports nothing; pressing the other one reports the new verdict, and
     * the reports read the last one for a given reader as what they settled on.
     */
    const answer = (given: JourneyFeedbackVerdict) => {
        if (given === verdict) {
            return;
        }

        setVerdict(given);

        trackViaDataLayer({
            event: JOURNEY_FEEDBACK_EVENT,
            verdict: given,
            model_id: modelId,
        });
    };

    const handleUsefulClick = () => answer(JourneyFeedbackVerdict.USEFUL);
    const handleNotUsefulClick = () =>
        answer(JourneyFeedbackVerdict.NOT_USEFUL);

    return (
        <StyledJourneyFeedback data-testid={dataTestId} {...otherProps}>
            {/*
             * The question becomes the answer to it, in the same size and colour
             * and on its own line, so saying thanks neither shouts nor moves the
             * buttons underneath.
             */}
            {hasAnswered ? (
                <p
                    className="journey-feedback-question"
                    // Announced to a screen reader, which would otherwise be told
                    // only that a button became pressed.
                    role="status"
                    data-testid={`${dataTestId}__thanks`}
                >
                    {t(
                        'Thanks for the feedback, it helps us work out what to keep'
                    )}
                </p>
            ) : (
                <p className="journey-feedback-question">
                    <Trans
                        components={{
                            'beta-text': (
                                <strong className="journey-feedback-beta" />
                            ),
                        }}
                    >
                        {t(
                            'This is a <beta-text>beta</beta-text> feature - was this useful?'
                        )}
                    </Trans>
                </p>
            )}

            {/*
             * Both buttons stay exactly where they were once one is pressed, at
             * the same size: the pressed one keeps its label and has a tick drawn
             * in beside it, in space the tick was already occupying invisibly.
             *
             * `aria-pressed` is what carries that to a screen reader, and it is
             * what the stylesheet keys the pressed state off too — there is no
             * separate class saying the same thing twice. Neither button is ever
             * disabled: an answer can be changed, so both have to stay pressable.
             */}
            <div className="journey-feedback-actions">
                <button
                    type="button"
                    className="journey-feedback-button"
                    aria-pressed={verdict === JourneyFeedbackVerdict.USEFUL}
                    onClick={handleUsefulClick}
                    data-testid={`${dataTestId}__useful`}
                >
                    <span className="journey-feedback-button-label">
                        {t('Useful')}
                    </span>
                    <FeedbackTick />
                </button>
                <button
                    type="button"
                    className="journey-feedback-button"
                    aria-pressed={verdict === JourneyFeedbackVerdict.NOT_USEFUL}
                    onClick={handleNotUsefulClick}
                    data-testid={`${dataTestId}__not-useful`}
                >
                    <span className="journey-feedback-button-label">
                        {t('Not useful')}
                    </span>
                    <FeedbackTick />
                </button>
            </div>

            <p
                className="journey-feedback-disclaimer"
                data-testid={`${dataTestId}__disclaimer`}
            >
                {t(
                    "This feature is in beta. We're still testing it, and some content is generated by AI. AI-generated content may be inaccurate, so check important information before relying on it."
                )}
            </p>
        </StyledJourneyFeedback>
    );
};
